import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card';
import { Users } from 'lucide-react';

interface RecruiterPerformance {
  recruiter: string;
  manager: string;
  totalSubmissions: number;
  fitmentScores: {
    '90-100': number;
    '80-90': number;
    '70-80': number;
    '60-70': number;
    '50-60': number;
    '40-50': number;
    '<40': number;
  };
}

interface RecruiterPerformanceTableProps {
  data: RecruiterPerformance[];
  isLoading?: boolean;
}

export const RecruiterPerformanceTable: React.FC<RecruiterPerformanceTableProps> = ({
  data,
  isLoading = false
}) => {
  if (isLoading) {
    return (
      <Card className="glass-card">
        <CardHeader>
          <div className="animate-pulse">
            <div className="h-6 bg-glass-surface dark:bg-glass-surface-dark-mode rounded mb-2"></div>
            <div className="h-4 bg-glass-surface dark:bg-glass-surface-dark-mode rounded w-2/3"></div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse">
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-4 bg-glass-surface dark:bg-glass-surface-dark-mode rounded"></div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass-card hover-lift">
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Users className="w-5 h-5 text-secondary-500" />
          <span>Recruiter Performance</span>
        </CardTitle>
        <CardDescription>
          Detailed performance metrics for each recruiter
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-glass-border dark:border-glass-border-dark-mode">
                <th className="text-left py-3 px-4 text-base font-semibold text-text-enterprise-primary">Recruiter</th>
                <th className="text-left py-3 px-4 text-base font-semibold text-text-enterprise-primary">Manager</th>
                <th className="text-center py-3 px-4 text-base font-semibold text-text-enterprise-primary">Total Uploads</th>
                <th className="text-center py-3 px-4 text-base font-semibold text-text-enterprise-primary">90-100</th>
                <th className="text-center py-3 px-4 text-base font-semibold text-text-enterprise-primary">80-90</th>
                <th className="text-center py-3 px-4 text-base font-semibold text-text-enterprise-primary">70-80</th>
                <th className="text-center py-3 px-4 text-base font-semibold text-text-enterprise-primary">60-70</th>
                <th className="text-center py-3 px-4 text-base font-semibold text-text-enterprise-primary">50-60</th>
                <th className="text-center py-3 px-4 text-base font-semibold text-text-enterprise-primary">40-50</th>
                <th className="text-center py-3 px-4 text-base font-semibold text-text-enterprise-primary">&lt;40</th>
              </tr>
            </thead>
            <tbody>
              {data.map((recruiter, index) => (
                <tr key={index} className="border-b border-glass-border/50 dark:border-glass-border-dark-mode/50">
                  <td className="py-3 px-4 text-base font-medium text-text-enterprise-primary">{recruiter.recruiter}</td>
                  <td className="py-3 px-4 text-base text-text-enterprise-secondary">{recruiter.manager}</td>
                  <td className="py-3 px-4 text-center text-base font-bold text-text-enterprise-primary">{recruiter.totalSubmissions}</td>
                  <td className="py-3 px-4 text-center text-base text-success-500">{recruiter.fitmentScores['90-100']}</td>
                  <td className="py-3 px-4 text-center text-base text-primary-500">{recruiter.fitmentScores['80-90']}</td>
                  <td className="py-3 px-4 text-center text-base text-secondary-500">{recruiter.fitmentScores['70-80']}</td>
                  <td className="py-3 px-4 text-center text-base text-warning-500">{recruiter.fitmentScores['60-70']}</td>
                  <td className="py-3 px-4 text-center text-base text-warning-600">{recruiter.fitmentScores['50-60']}</td>
                  <td className="py-3 px-4 text-center text-base text-error-500">{recruiter.fitmentScores['40-50']}</td>
                  <td className="py-3 px-4 text-center text-base text-error-600">{recruiter.fitmentScores['<40']}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default RecruiterPerformanceTable;
